import { useId, useRef, type MutableRefObject } from 'react';
import { Textarea } from '@ev-tech/ui-core-components/Textarea';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';
import { Copy } from '@ev-tech/ui-core-components/Copy';

import { useChatbotStore } from '../../stores/useChatbot';
import { useChatbotSession } from '../../providers/ChatbotProvider';
import PaperplaneIcon from '../../assets/Paperplane.svg';

import styles from './ChatbotComposer.module.css';
import accessibilityStyles from '../../styles/accessibility.module.css';

type ChatbotComposerProps = {
  isPending: boolean;
  inputRef: MutableRefObject<HTMLTextAreaElement | null>;
  onSendMessage: (message: string) => void;
};

export const ChatbotComposer = ({ isPending, inputRef, onSendMessage }: ChatbotComposerProps) => {
  const { t } = useChatbotSession();
  const draft = useChatbotStore((state) => state.draft);
  const setDraft = useChatbotStore((state) => state.setDraft);
  const inputId = useId();
  const composing = useRef(false);

  const submit = () => {
    if (!isPending) onSendMessage(draft);
  };

  return (
    <form
      className={styles.composer}
      aria-busy={isPending}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      onCompositionStart={() => {
        composing.current = true;
      }}
      onCompositionEnd={() => {
        composing.current = false;
      }}
      onKeyDown={(event) => {
        if (
          event.target !== inputRef.current ||
          event.key !== 'Enter' ||
          event.shiftKey ||
          event.nativeEvent.isComposing ||
          composing.current ||
          event.keyCode === 229
        )
          return;

        event.preventDefault();
        submit();
      }}
    >
      <div className={styles.composerField}>
        <Textarea
          ref={inputRef}
          id={inputId}
          name={inputId}
          rows={1}
          label={<span className={accessibilityStyles.srOnly}>{t('chatbot.input')}</span>}
          placeholder={t('chatbot.input')}
          value={draft}
          disabled={isPending}
          onChange={(event) => setDraft(event.target.value)}
          className={styles.textarea}
        />
        <div className={styles.send}>
          <IconButton
            as="button"
            type="submit"
            icon={<PaperplaneIcon />}
            aria-label={t('chatbot.send')}
            shape="square"
            size="small"
            variant="search"
            disabled={isPending || !draft.trim()}
          />
        </div>
      </div>
      <Copy size="s" className={styles.disclaimer} text={t('chatbot.disclaimer')} />
    </form>
  );
};
